import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useCreateLitter } from './api/useCreateLitter';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DogSelect } from '@/components/ui/dog-select';
import { ImageUpload } from '@/components/ui/image-upload';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import { COLOR_CHECKBOX_OPTIONS } from '@/shared/constants';

const addLitterSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  litterType: z.enum(['planned', 'upcoming', 'past']).optional(),
  sireId: z.string().min(1, 'Sire is required'),
  damId: z.string().min(1, 'Dam is required'),
  // Planned litter fields
  expectedMatingDate: z.string().optional(),
  expectedDueMonth: z.string().optional(),
  // Upcoming litter fields
  expectedDueDate: z.string().optional(),
  expectedPuppyDate: z.string().optional(),
  // Past litter fields
  dobOfPuppies: z.string().optional(),
  // Common fields
  limitedRegistrationPrice: z.number().optional(),
  mainRegistrationPrice: z.number().optional(),
  expectedColors: z.array(z.string()).default([]),
  content: z.string().optional(),
  imagesUrls: z.array(z.string()).optional(),
});

type AddLitterFormData = z.infer<typeof addLitterSchema>;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function AddLitterDialog({ open, onOpenChange }: Props) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<AddLitterFormData>({
    resolver: zodResolver(addLitterSchema),
    defaultValues: {
      name: '',
      litterType: 'planned',
      sireId: '',
      damId: '',
      expectedMatingDate: '',
      expectedDueMonth: '',
      expectedDueDate: '',
      expectedPuppyDate: '',
      dobOfPuppies: '',
      limitedRegistrationPrice: undefined,
      mainRegistrationPrice: undefined,
      expectedColors: [],
      content: '',
      imagesUrls: [],
    },
  });

  const litterType = watch('litterType');
  const expectedColors = watch('expectedColors') || [];

  const mutation = useCreateLitter({
    config: {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'Litter created successfully',
        });
        reset();
        onOpenChange(false);
      },
      onError: (error: Error) => {
        console.error('Mutation error:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to create litter',
          variant: 'destructive',
        });
      },
    },
  });

  useEffect(() => {
    if (!open) {
      reset({
        name: '',
        litterType: 'planned',
        sireId: '',
        damId: '',
        expectedMatingDate: '',
        expectedDueMonth: '',
        expectedDueDate: '',
        expectedPuppyDate: '',
        dobOfPuppies: '',
        limitedRegistrationPrice: undefined,
        mainRegistrationPrice: undefined,
        expectedColors: [],
        content: '',
        imagesUrls: [],
      });
    }
  }, [open, reset]);

  const onSubmit = (data: AddLitterFormData) => {
    const apiPayload: Record<string, unknown> = {
      name: data.name,
      sireId: data.sireId,
      damId: data.damId,
      litterType: data.litterType || 'planned',
      expectedColors: data.expectedColors || [],
      content: data.content || undefined,
      imagesUrls: data.imagesUrls || undefined,
      limitedRegistrationPrice: data.limitedRegistrationPrice || undefined,
      mainRegistrationPrice: data.mainRegistrationPrice || undefined,
    };

    if (data.litterType === 'planned') {
      apiPayload.expectedMatingDate = data.expectedMatingDate || undefined;
      apiPayload.expectedDueMonth = data.expectedDueMonth || undefined;
    }
    if (data.litterType === 'upcoming') {
      apiPayload.expectedDueDate = data.expectedDueDate || undefined;
      apiPayload.expectedPuppyDate = data.expectedPuppyDate || undefined;
    }
    if (data.litterType === 'past') {
      apiPayload.dobOfPuppies = data.dobOfPuppies || undefined;
    }

    Object.keys(apiPayload).forEach((key) => {
      if (apiPayload[key] === undefined) {
        delete apiPayload[key];
      }
    });

    mutation.mutate(apiPayload);
  };

  const handleColorToggle = (colorValue: string, checked: boolean) => {
    const currentColors = expectedColors || [];
    if (checked) {
      if (colorValue === 'any') {
        setValue('expectedColors', ['any']);
      } else {
        const filteredColors = currentColors.filter((c) => c !== 'any');
        if (!filteredColors.includes(colorValue)) {
          setValue('expectedColors', [...filteredColors, colorValue]);
        }
      }
    } else {
      const filteredColors = currentColors.filter((c) => c !== colorValue);
      setValue('expectedColors', filteredColors);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Litter</DialogTitle>
          <DialogDescription>
            Create a new litter record. Fill in the details below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input id="name" {...register('name')} placeholder="Enter litter name" />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="litterType">Type</Label>
              <Select
                value={litterType || 'planned'}
                onValueChange={(value) =>
                  setValue('litterType', value as 'planned' | 'upcoming' | 'past')
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="past">Past</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DogSelect
              value={watch('sireId')}
              onValueChange={(value) => setValue('sireId', value)}
              sex="Male"
              label={
                <>
                  Sire <span className="text-destructive">*</span>
                </>
              }
            />
            {errors.sireId && (
              <p className="text-sm text-destructive col-span-2">{errors.sireId.message}</p>
            )}

            <DogSelect
              value={watch('damId')}
              onValueChange={(value) => setValue('damId', value)}
              sex="Female"
              label={
                <>
                  Dam <span className="text-destructive">*</span>
                </>
              }
            />
            {errors.damId && (
              <p className="text-sm text-destructive col-span-2">{errors.damId.message}</p>
            )}
          </div>

          {/* Planned Litter Fields */}
          {litterType === 'planned' && (
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold">Planned Litter Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="expectedMatingDate">Expected Mating Month</Label>
                  <Input
                    id="expectedMatingDate"
                    {...register('expectedMatingDate')}
                    placeholder="e.g., January 2024"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expectedDueMonth">Expected Due Month</Label>
                  <Input
                    id="expectedDueMonth"
                    {...register('expectedDueMonth')}
                    placeholder="e.g., March 2024"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Upcoming Litter Fields */}
          {litterType === 'upcoming' && (
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold">Upcoming Litter Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <DatePicker control={control} name="expectedDueDate" label="Expected Due Date" />
                <DatePicker
                  control={control}
                  name="expectedPuppyDate"
                  label="Expected Puppy Date"
                />
              </div>
              <ImageUpload
                control={control}
                name="imagesUrls"
                label="Photos"
                keyPrefix="litters"
                description="Upload multiple images (JPG, PNG)"
              />
            </div>
          )}

          {/* Past Litter Fields */}
          {litterType === 'past' && (
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold">Past Litter Details</h3>
              <DatePicker control={control} name="dobOfPuppies" label="DOB of Puppies" />
              <ImageUpload
                control={control}
                name="imagesUrls"
                label="Photos"
                keyPrefix="litters"
                description="Upload multiple images (JPG, PNG)"
              />
            </div>
          )}

          {/* Common Fields */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-lg font-semibold">Additional Information</h3>

            {(litterType === 'planned' || litterType === 'upcoming') && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="limitedRegistrationPrice">Limited Registration Price</Label>
                    <Input
                      id="limitedRegistrationPrice"
                      type="number"
                      step="0.01"
                      {...register('limitedRegistrationPrice', { valueAsNumber: true })}
                      placeholder="Enter price"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mainRegistrationPrice">Main Registration Price</Label>
                    <Input
                      id="mainRegistrationPrice"
                      type="number"
                      step="0.01"
                      {...register('mainRegistrationPrice', { valueAsNumber: true })}
                      placeholder="Enter price"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Expected Colors</Label>
                  <div className="space-y-2">
                    {COLOR_CHECKBOX_OPTIONS.map((colorOption) => (
                      <div key={colorOption.value} className="flex items-center gap-2">
                        <Checkbox
                          id={`color-${colorOption.value}`}
                          checked={expectedColors.includes(colorOption.value)}
                          disabled={colorOption.value !== 'any' && expectedColors.includes('any')}
                          onChange={(e) => handleColorToggle(colorOption.value, e.target.checked)}
                          className="mt-0"
                        />
                        <Label
                          htmlFor={`color-${colorOption.value}`}
                          className="font-normal cursor-pointer leading-none"
                        >
                          {colorOption.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content">Content</Label>
                  <RichTextEditor
                    value={watch('content') || ''}
                    onChange={(content: string) => {
                      setValue('content', content);
                    }}
                    height={500}
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {isSubmitting || mutation.isPending ? 'Creating...' : 'Create Litter'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
